import { useState } from 'react';
import { Collapse, Table, Tag, Switch, Input, Button, Space, Popconfirm, Typography, App } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAiProviders, updateAiProvider, testAiProvider, type AiProvider,
} from '@/api/aiProviders';
import { getAiModels, deleteAiModel, type AiModel } from '@/api/aiModels';
import { ModelFormModal } from './ModelFormModal';
import { groupModelsByProvider, defaultOpenPanels } from './modelGrouping';

const SOURCE_TAG: Record<AiProvider['source'], { color: string; text: string }> = {
  db: { color: 'green', text: 'configured' },
  env: { color: 'blue', text: 'env fallback' },
  none: { color: 'default', text: 'not configured' },
};

export function ProvidersModelsSection() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const providersQ = useQuery({ queryKey: ['ai-providers'], queryFn: getAiProviders });
  const modelsQ = useQuery({ queryKey: ['ai-models'], queryFn: getAiModels });

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string>('');
  const [openKeys, setOpenKeys] = useState<string[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProvider, setModalProvider] = useState<string | null>(null);
  const [editing, setEditing] = useState<AiModel | null>(null);

  const providers = providersQ.data ?? [];
  const byProvider = groupModelsByProvider(modelsQ.data ?? []);
  const activeKeys = openKeys ?? defaultOpenPanels(providers);

  const refreshProviders = () => qc.invalidateQueries({ queryKey: ['ai-providers'] });
  const refreshModels = () => {
    qc.invalidateQueries({ queryKey: ['ai-models'] });
    qc.invalidateQueries({ queryKey: ['ai-providers'] });
  };

  async function saveKey(p: AiProvider) {
    const api_key = drafts[p.provider] ?? '';
    if (!api_key) { message.warning('Enter a key first'); return; }
    setBusy(p.provider + ':save');
    try {
      await updateAiProvider(p.provider, { api_key });
      setDrafts((d) => ({ ...d, [p.provider]: '' }));
      message.success('Saved');
      refreshProviders();
    } catch (e) { message.error((e as Error).message); } finally { setBusy(''); }
  }
  async function toggle(p: AiProvider, enabled: boolean) {
    setBusy(p.provider + ':toggle');
    try { await updateAiProvider(p.provider, { enabled }); refreshProviders(); }
    catch (e) { message.error((e as Error).message); } finally { setBusy(''); }
  }
  async function runTest(p: AiProvider) {
    setBusy(p.provider + ':test');
    try {
      const r = await testAiProvider(p.provider);
      if (r.ok) message.success(`${p.provider}: key works`);
      else message.error(`${p.provider}: ${r.message ?? r.error ?? 'test failed'}`);
    } catch (e) { message.error((e as Error).message); } finally { setBusy(''); }
  }
  async function removeModel(m: AiModel) {
    try { await deleteAiModel(m.model); message.success('Deleted'); refreshModels(); }
    catch (e) { message.error((e as Error).message); }
  }
  function openAdd(provider: string) { setEditing(null); setModalProvider(provider); setModalOpen(true); }
  function openEdit(m: AiModel) { setEditing(m); setModalProvider(m.provider); setModalOpen(true); }

  const items = providers.map((p) => ({
    key: p.provider,
    label: (
      <Space wrap>
        <b style={{ textTransform: 'capitalize' }}>{p.provider}</b>
        <Tag color={SOURCE_TAG[p.source].color}>{SOURCE_TAG[p.source].text}</Tag>
        {p.hasKey && <Typography.Text type="secondary">{p.keyMasked}</Typography.Text>}
        <Typography.Text type="secondary">· {(byProvider[p.provider] ?? []).length} model(s)</Typography.Text>
      </Space>
    ),
    extra: (
      <Switch
        checkedChildren="on"
        unCheckedChildren="off"
        checked={p.enabled}
        loading={busy === p.provider + ':toggle'}
        onClick={(_, e) => e.stopPropagation()}
        onChange={(v) => toggle(p, v)}
      />
    ),
    children: (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <Space.Compact style={{ width: 300 }}>
            <Input.Password
              placeholder="paste new key"
              value={drafts[p.provider] ?? ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [p.provider]: e.target.value }))}
            />
            <Button type="primary" loading={busy === p.provider + ':save'} onClick={() => saveKey(p)}>
              Save key
            </Button>
          </Space.Compact>
          <Button loading={busy === p.provider + ':test'} disabled={!p.hasKey} onClick={() => runTest(p)}>
            Test
          </Button>
        </Space>
        <Table<AiModel>
          rowKey="model"
          dataSource={byProvider[p.provider] ?? []}
          pagination={false}
          size="small"
          columns={[
            { title: 'Model', dataIndex: 'model' },
            { title: 'API model id', dataIndex: 'apiModelId' },
            { title: 'In $/1M', dataIndex: 'inputPrice' },
            { title: 'Out $/1M', dataIndex: 'outputPrice' },
            { title: 'Vision', dataIndex: 'supportsVision', render: (v) => (v ? '✓' : '') },
            { title: 'PDF', dataIndex: 'acceptsPdfNatively', render: (v) => (v ? '✓' : '') },
            { title: 'Enabled', dataIndex: 'enabled', render: (v) => (v ? '✓' : '—') },
            {
              title: 'Actions',
              render: (_, m) => (
                <Space>
                  <Button size="small" onClick={() => openEdit(m)}>Edit</Button>
                  <Popconfirm title={`Delete ${m.model}?`} onConfirm={() => removeModel(m)}>
                    <Button size="small" danger>Delete</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
        <div>
          <Button type="dashed" onClick={() => openAdd(p.provider)}>+ Add model to {p.provider}</Button>
        </div>
      </Space>
    ),
  }));

  return (
    <>
      <Collapse
        activeKey={activeKeys}
        onChange={(k) => setOpenKeys(k as string[])}
        items={items}
      />
      <ModelFormModal
        open={modalOpen}
        editing={editing}
        providerLocked={modalProvider ?? undefined}
        onClose={() => setModalOpen(false)}
        onSaved={refreshModels}
      />
    </>
  );
}
